import type { JobHistoryEventResponseType } from '../JobApi';
import { JobHistoryUtils } from './JobHistoryUtils';
import type { JobHistoryItemType } from './types';
import { availableProcessStages } from '../../../../stores/Const';
import type { TacticalPlanRule } from '../../TacticalPlan/TacticalPlanRule';

export interface IJobHistoryItem {
    _eventResponse: JobHistoryEventResponseType;
    getEventDescription(): string;
}

export class JobHistoryItem implements IJobHistoryItem {
    _eventResponse: JobHistoryEventResponseType;
    diffs: JobHistoryItemType[];
    lastVehicleId: number | null = null;
    lastVehicleName: string | null = null;
    lastProcessStatusId: number | null = null;
    _isVisible: boolean = false;

    constructor(d: JobHistoryEventResponseType) {
        this._eventResponse = d;
        this.diffs = JobHistoryUtils.extractAllValues(this._eventResponse.diff_data);
    }

    setNewVehicleId(id: number) {
        this.lastVehicleId = id;
    }

    setNewProcessStatusId(id: number) {
        this.lastProcessStatusId = id;
    }

    setNewVehicleName(name: string) {
        this.lastVehicleName = name;
    }

    getData() {
        return this._eventResponse;
    }

    getEventDescription(): string {
        return this._eventResponse.event_description;
    }

    isProcessIncreasing(): boolean | null {
        let processStageExists = this.diffs.some(d => d.id === 'process_stage');
        if (!processStageExists) {
            return null;
        }
        if (this.getNewProcessStatusId() === null) {
            return null;
        }
        if (this.getOldProcessStatusId() === null) {
            return null;
        }
        return this.getNewProcessStatusId(false) > this.getOldProcessStatusId();
    }

    isProcessDecreasing(): boolean | null {
        let processStageExists = this.diffs.some(d => d.id === 'process_stage');
        if (!processStageExists) {
            return null;
        }
        if (this.getNewProcessStatusId() === null) {
            return null;
        }
        if (this.getOldProcessStatusId() === null) {
            return null;
        }
        return this.getNewProcessStatusId(false) < this.getOldProcessStatusId();
    }

    isVisible() {
        return this._isVisible;
    }

    setVisible(visible: boolean) {
        this._isVisible = visible;
    }

    getEventChanger(): string | null {
        return (
            this._eventResponse?.user_email?.toString() ||
            this._eventResponse?.user_id?.toString() ||
            null
        );
    }

    getNewProcessStatusId(useLastProcessStatusId: boolean = true): number | null {
        return (
            // this.diffs.find(d => d.id === 'process_status')?.new ??
            this.diffs.find(d => d.id === 'process_stage')?.new ??
            (useLastProcessStatusId ? this.lastProcessStatusId : null)
        );
    }

    getOldProcessStatusId(): number | null {
        return this.diffs.find(d => d.id === 'process_stage')?.old ?? null;
    }

    getNewProcessStatusName(): string | null {
        return (
            availableProcessStages.find(x => x.id === this.getNewProcessStatusId())?.name || null
        );
    }

    getNewProcessStatusDesc(): string | null {
        return (
            availableProcessStages.find(x => x.id === this.getNewProcessStatusId())?.desc || null
        );
    }

    getNewVehicleId(useLastVehicleId: boolean = true): number | null {
        let id: number | null = null;
        id = this.diffs.find(d => d.id === 'vehicle_id')?.new || null;
        if (id === null) {
            id = this.diffs.find(d => d.new?.vehicle_id)?.new?.vehicle_id || null;
        }
        if (id === null && useLastVehicleId) {
            id = this.lastVehicleId;
        }
        if (id === null) {
            id = this.getNewSuggestedVehicleId();
        }
        return id;
    }

    getNewVehicleName(): string | null {
        let name: string | null = null;
        name = this.diffs.find(d => d.id === 'vehicle.name')?.new || null;
        if (name === null) {
            name = this.diffs.find(d => d.new?.vehicle?.name)?.new?.vehicle?.name || null;
        }
        if (name === null) {
            name = this.lastVehicleName;
        }
        return name;
    }

    // getNewVehicleName(): string | null {
    //     let keys: string[] = Object.keys(this._eventResponse?.diff_data);
    //     let name: string | null = null;
    //     try {
    //         keys.forEach(key => {
    //             let n =
    //                 (this._eventResponse.diff_data[key].new &&
    //                     this._eventResponse.diff_data[key].new[0]?.vehicle?.name) ||
    //                 null;
    //             if (n) {
    //                 name = n;
    //             }
    //         });
    //     } catch (e) {
    //         console.warn('getNewVehicleName', e);
    //     }
    //     if (name === null) {
    //         name =
    //             get(vehiclesManager)
    //                 .getItems()
    //                 .find(v => v.getId() === this.getNewVehicleId())
    //                 ?.getName() || null;
    //     }
    //     if (name === null) {
    //         name = this.lastVehicleName;
    //     }
    //     if (name === null) {
    //         name = this.getNewVehicleId()?.toString() || null;
    //     }
    //     return name;
    // }

    getTacticalPLanRuleId(): number | null {
        let id = this.diffs.find(d => d.id === 'tactical_plan_rule_id')?.new ?? null;
        if (id === null) {
            id =
                this.diffs.find(d => d.new?.tactical_plan_rule_id)?.new?.tactical_plan_rule_id ||
                null;
        }
        return id;
    }

    getTacticalPlanRuleName(tacticalPlanRules: TacticalPlanRule[] | null): string | null {
        if (tacticalPlanRules === null) {
            let name = this.diffs.find(d => d.id === 'tactical_plan_rule_name')?.new ?? null;
            if (name === null) {
                name =
                    this.diffs.find(d => d.new?.tactical_plan_rule_name)?.new
                        ?.tactical_plan_rule_name || null;
            }
            return name;
        } else {
            let rule =
                tacticalPlanRules.find(rule => rule.getId() === this.getTacticalPLanRuleId()) ??
                null;
            if (rule) {
                if (rule.getName() !== '') {
                    return rule.getName();
                }
            }
        }
        return null;
    }

    getTacticalPlanId(tacticalPlanRules: TacticalPlanRule[]): number | null {
        let rule =
            tacticalPlanRules.find(rule => rule.getId() === this.getTacticalPLanRuleId()) ?? null;
        if (rule) {
            return rule.getTacticalPlanId();
        }
        return null;
    }

    getTacticalPlanName(): string | null {
        // console.warn('this.diffs', this.diffs);
        return this.diffs.find(d => d.new?.tactical_plan_name)?.new?.tactical_plan_name ?? null;
    }

    getTacticalPlanRule(tacticalPlanRules: TacticalPlanRule[]): TacticalPlanRule | null {
        return (
            tacticalPlanRules.find(rule => rule.getId() === this.getTacticalPLanRuleId()) ?? null
        );
    }

    isTacticalPlanRuleAutoAccepted(tacticalPlanRules: TacticalPlanRule[]): boolean {
        let rule =
            tacticalPlanRules.find(rule => rule.getId() === this.getTacticalPLanRuleId()) ?? null;
        if (rule) {
            return rule.getAutoConfirm();
        }
        return false;
    }

    getZoneId(): number | null {
        let id = this.diffs.find(d => d.id === 'zone_id')?.new ?? null;
        if (id === null) {
            id = this.diffs.find(d => d.new?.zone_id)?.new?.zone_id || null;
        }
        return id;
    }

    getZoneName(): string | null {
        return this.diffs.find(d => d?.new?.name)?.new?.name ?? null;
    }
    // getZoneName(mapZones: ZoneType[] | null): string | null {
    //     if (mapZones === null) {
    //         let name = this.diffs.find(d => d?.new?.name)?.new?.name ?? null;
    //         return name;
    //     } else {
    //         let zoneName: string | null =
    //             mapZones.find(z => z.id === this.getZoneId())?.name ?? null;
    //         zoneName = zoneName !== '' ? zoneName : null;
    //         return zoneName;
    //     }
    // }

    isConfirmRejected(): boolean {
        // return this.isProcessDecreasing() === true && this.getNewProcessStatusId(false) === 0;
        // return (
        //     this._eventResponse.event_type === 'confirm' &&
        //     this._eventResponse.diff_data?.assignments?.new?.length === 0
        // );
        return this._eventResponse.event_type === 'reject';
    }

    getNewSuggestedVehicleId(): number | null {
        return this.diffs?.find(d => d.id === 'suggestion_vehicle_id')?.new ?? null;
    }
}
